import { calculateAspectRatio } from "@msuite/utils/files";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { Fragment, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../button";
import { Icon } from "../icons";
import { useTranslation } from "../localization";
import { ConfirmModal, useAsyncModal } from "../modal";
import type { FileUploadProps } from "./file-upload";

/** Props Interface */
interface ImageUploadProps extends FileUploadProps {
	type: "logo" | "image";
	defaultImageUrl?: string | null;
	onRemove?: () => void;
	"data-cy"?: string;
}

export function ImageUpload({
	controller,
	type,
	defaultImageUrl = null,
	onRemove,
	"data-cy": dataCy,
}: ImageUploadProps) {
	/** Computed */
	const resolvedDefaultImageUrl = defaultImageUrl ?? controller.defaultFileUrl;

	/** State */
	const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(resolvedDefaultImageUrl);
	const [currentFileDimensions, setCurrentFileDimensions] = useState<{
		width: number;
		height: number;
	} | null>(null);

	/** Hooks */
	const { t } = useTranslation();
	const deleteImageAsyncModal = useAsyncModal();
	const dropzone = useDropzone({
		accept: {
			"image/gif": [".gif"],
			"image/jpeg": [".jpeg", ".jpg", ".JPEG", ".JPG"],
			"image/png": [".png"],
		},
		maxFiles: 1,
		onDrop: (file) => {
			controller.handleOnReset();
			controller.handleOnAddFiles(file);
		},
	});

	/** Functions */
	async function handleOnRemove() {
		const isConfirmed = await deleteImageAsyncModal.confirm();
		if (!isConfirmed) return;
		onRemove?.();
		const currentFile = controller.files[0];
		if (!currentFile) return;
		controller.handleOnRemove(currentFile);
	}

	/** Effects */
	useEffect(() => {
		const currentFile = controller.files[0];
		if (!currentFile) {
			setCurrentFileUrl(resolvedDefaultImageUrl);
			setCurrentFileDimensions(null);
		} else {
			const currentFileUrl = URL.createObjectURL(currentFile);
			setCurrentFileUrl(currentFileUrl);
			const img = new Image();
			img.src = currentFileUrl;
			img.onload = () => {
				setCurrentFileDimensions({
					width: img.width,
					height: img.height,
				});
			};
			return () => {
				URL.revokeObjectURL(currentFileUrl);
			};
		}
	}, [controller.files, resolvedDefaultImageUrl]);

	useEffect(() => {
		if (!resolvedDefaultImageUrl) return;
		if (currentFileDimensions) return;
		const img = new Image();
		img.src = resolvedDefaultImageUrl;
		img.onload = () => {
			setCurrentFileDimensions({
				width: img.width,
				height: img.height,
			});
		};
	}, [resolvedDefaultImageUrl, currentFileDimensions]);

	/** Computed */
	const label: Parameters<ReturnType<typeof useTranslation>["t"]>[0] =
		type === "image" ? "file_upload.upload_image" : "file_upload.upload_logo";

	const hasFile = !!currentFileUrl;

	/** Render */
	return (
		<Fragment>
			<div
				className="flex flex-row items-center gap-4 cursor-pointer"
				{...(dropzone.getRootProps() as any)}
			>
				<input
					{...dropzone.getInputProps()}
					multiple
					className="hidden"
					data-cy={dataCy}
				/>
				<div
					className={clsx(
						"bg-primaryGray-50 dark:bg-primaryGray-900 rounded-base h-14 max-w-[150px]",
						"border border-color",
						"flex-center",
						"text-color",
						"focus:input-focused",
						"hover:input-focused",
						"text-color",
						"relative overflow-hidden",
					)}
					style={{
						aspectRatio: calculateAspectRatio(
							currentFileDimensions?.width,
							currentFileDimensions?.height,
						),
					}}
				>
					<AnimatePresence mode="popLayout">
						{currentFileUrl ? (
							<motion.div
								key={currentFileUrl}
								className="top-0 left-0 absolute rounded-md w-full h-full card-bg"
								style={{
									backgroundImage: `url(${currentFileUrl})`,
									backgroundSize: "cover",
								}}
								initial={{ opacity: 0, scale: 0 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0 }}
								transition={{ duration: 0.4, ease: "circInOut" }}
							/>
						) : (
							<motion.div
								key="image-upload-icon"
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								exit={{ scale: 0 }}
								transition={{ duration: 0.4, ease: "circInOut" }}
							>
								<Icon
									name="CloudUpload"
									className="text-2xl"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
				<div className="flex flex-col gap-0">
					<p className="text-sm">{t(label)}</p>
					<p className="opacity-50 text-xs">{t("hints.you_can_change_this_at_any_time")}</p>
				</div>
				<div className="flex-1" />
				<AnimatePresence mode="wait">
					{hasFile && (
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 10 }}
							transition={{ duration: 0.2, ease: "circInOut" }}
						>
							{onRemove && (
								<Button
									icon={<Icon name="Trash2" />}
									variant="danger-ghost"
									onClick={handleOnRemove}
									stopPropagation
								/>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			{deleteImageAsyncModal.isOpen && (
				<ConfirmModal
					controller={deleteImageAsyncModal}
					message={t("file_upload.delete_file_confirm_message")}
				/>
			)}
		</Fragment>
	);
}
